import React from "react";

// Building blocks for the scenario settings: every setting gets
//   1. label + compact control on one line
//   2. one short sentence below (what it does)
//   3. an optional "Example" that expands (diagram + real numbers)
// Native <details> for the example: no state, keyboard accessible.

/** A titled group with an optional one-line intro ("when does this matter"). */
export function SettingGroup({ title, intro, children }) {
  return (
    <section className="ssp-group">
      <h4 className="ssp-group__title">{title}</h4>
      {intro && <p className="ssp-group__intro">{intro}</p>}
      <div className="ssp-group__body">{children}</div>
    </section>
  );
}

function Example({ example }) {
  if (!example) return null;
  return (
    <details className="ssp-example">
      <summary>Example</summary>
      <div className="ssp-example__body">{example}</div>
    </details>
  );
}

// "Not used in Section Oriented" etc.: the setting stays editable, but is
// greyed out and says why changing it has no effect right now.
function InactiveNote({ note }) {
  if (!note) return null;
  return <p className="ssp-field__inactive">{note}</p>;
}

/**
 * Label left, control right (number inputs). `stacked` puts the control
 * below the label at full width (dropdowns with long entries).
 */
export function SettingField({ id, label, hint, example, disabled, inactiveNote, stacked = false, children }) {
  return (
    <div
      className={
        "ssp-field" +
        (disabled ? " ssp-field--disabled" : "") +
        (inactiveNote ? " ssp-field--inactive" : "") +
        (stacked ? " ssp-field--stacked" : "")
      }
    >
      <div className="ssp-field__row">
        <label className="ssp-field__label" htmlFor={id}>{label}</label>
        {!stacked && <div className="ssp-field__control">{children}</div>}
      </div>
      {stacked && <div className="ssp-field__control ssp-field__control--stacked">{children}</div>}
      <InactiveNote note={inactiveNote} />
      {hint && <p className="ssp-field__hint">{hint}</p>}
      <Example example={example} />
    </div>
  );
}

/** Checkbox + label on one line, hint indented below the label. */
export function SettingToggle({ id, label, hint, example, checked, disabled, inactiveNote, onChange }) {
  return (
    <div
      className={
        "ssp-field ssp-field--toggle" +
        (disabled ? " ssp-field--disabled" : "") +
        (inactiveNote ? " ssp-field--inactive" : "")
      }
    >
      <label className="ssp-toggle" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        <span className="ssp-field__label">{label}</span>
      </label>
      <InactiveNote note={inactiveNote} />
      {hint && <p className="ssp-field__hint">{hint}</p>}
      <Example example={example} />
    </div>
  );
}

/**
 * Compact number input with an optional unit after it ("40 mm").
 * Keeps a draft while typing, so the field can be emptied and retyped without
 * jumping to 0; valid values are saved right away, an empty / invalid field
 * goes back to the stored value when it loses focus.
 */
export function NumberInput({ id, value, onChange, min = 0, max, step = 1, unit, disabled, integer = false }) {
  const [draft, setDraft] = React.useState(String(value ?? ""));
  const focused = React.useRef(false);

  React.useEffect(() => {
    if (!focused.current) setDraft(String(value ?? ""));
  }, [value]);

  const parse = (text) => {
    if (String(text).trim() === "") return null;
    const n = Number(text);
    if (!Number.isFinite(n)) return null;
    let v = Math.max(min ?? -Infinity, n);
    if (max !== undefined) v = Math.min(max, v);
    return integer ? Math.round(v) : v;
  };

  return (
    <span className="ssp-number">
      <input
        id={id}
        type="number"
        inputMode={integer ? "numeric" : "decimal"}
        min={min}
        max={max}
        step={step}
        value={draft}
        disabled={disabled}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => {
          setDraft(e.target.value);
          const v = parse(e.target.value);
          if (v !== null && v !== value) onChange?.(v);
        }}
        onBlur={() => {
          focused.current = false;
          const v = parse(draft);
          setDraft(String(v ?? value ?? ""));
        }}
        onWheel={(e) => e.currentTarget.blur()}
      />
      {/* always rendered, so inputs with and without a unit line up */}
      <span className="ssp-number__unit" aria-hidden={!unit}>{unit ?? ""}</span>
    </span>
  );
}

/** Example text with an optional diagram above it. */
export function ExampleBlock({ diagram, children }) {
  return (
    <>
      {diagram && <div className="ssp-example__diagram">{diagram}</div>}
      <div className="ssp-example__text">{children}</div>
    </>
  );
}
